import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Ban,
  Bot,
  CalendarPlus,
  CircleCheck,
  Clock,
  CreditCard,
  ExternalLink,
  FileText,
  Link2,
  LogIn,
  RotateCcw,
  Salad,
  StickyNote,
} from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { Modal, StatusChip } from "../components/kit";
import { Alert, ICON } from "../components/ui";
import {
  day,
  Filter,
  fiscalOf,
  IMPORT_LABEL,
  Kpi,
  Loadable,
  money,
  PageHead,
  PRODUCT_LABEL,
  post,
  RestaurantChip,
  ROLE_LABEL,
  type Row,
  SearchBox,
  Section,
  SubChip,
  Table,
  text,
  usd,
  useAdmin,
  when,
} from "./ui";

// Restaurante: the list with what support needs at a glance (subscription, activity, AI) and the 360° page of
// one restaurant with the support actions (suspend, trial, add-on, AI counter, notes).

const SUB_FILTERS: [string, string][] = [
  ["", "Toate"],
  ["trialing", "Probă gratuită"],
  ["active", "Plătesc"],
  ["past_due", "Plată restantă"],
  ["canceled", "Anulate"],
  ["expired", "Expirate"],
  ["beta", "Acces gratuit"],
  ["suspended", "Suspendate"],
];

export function Restaurants() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const sub = params.get("sub") ?? "";
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };
  const list = useAdmin<{ restaurants: Row[]; aiCapMicro: number }>(
    ["restaurants", q, sub],
    `/restaurants?q=${encodeURIComponent(q)}&sub=${sub}`,
  );
  return (
    <>
      <PageHead title="Restaurante" sub="Abonament, activitate și consum AI pentru fiecare client." />
      <PreparedClients />
      <div className="row" style={{ gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
        <SearchBox
          value={q}
          onChange={(v) => set("q", v)}
          label="Caută după nume, slug sau e-mailul proprietarului"
        />
        <Filter label="Abonament" value={sub} onChange={(v) => set("sub", v)} options={SUB_FILTERS} />
      </div>
      <Loadable q={list}>
        {(d) => (
          <>
            <p className="caption muted" style={{ margin: 0 }}>
              {d.restaurants.length} restaurante
            </p>
            <Table
              head={["Restaurant", "Proprietar", "Abonament", "Meniu", "Deschideri 30 z", "AI", "Creat"]}
              rows={d.restaurants.map((r) => [
                <div key="n" className="stack" style={{ gap: 2 }}>
                  <Link to={`/admin/restaurante/${String(r.id)}`} style={{ fontWeight: 600 }}>
                    {text(r.name)}
                  </Link>
                  {r.status !== "active" && <RestaurantChip status={r.status} />}
                </div>,
                text(r.owner_email),
                <SubChip key="s" status={r.sub_status} trialEndsAt={r.trial_ends_at} />,
                Number(r.published)
                  ? `publicat · ${text(r.products)} produse`
                  : `nepublicat · ${text(r.products)} produse`,
                text(r.views30),
                `${usd(r.ai_used_micro)} / ${usd(d.aiCapMicro, 0)}`,
                day(r.created_at),
              ])}
            />
          </>
        )}
      </Loadable>
    </>
  );
}

interface R360 {
  restaurant: Row;
  publicUrl: string;
  subscription: Row | null;
  billingProfile: Row | null;
  invoices: Row[];
  members: Row[];
  menus: Row[];
  imports: Row[];
  nutrition: Row[];
  feedback: Row & { recent: Row[] };
  views30: Row | null;
  ai: { usedMicro: number; capMicro: number; recent: Row[] };
  notes: Row[];
  audit: Row[];
}

type Dialog = null | "suspend" | "trial" | "nutrition" | "ai";

export function RestaurantDetail() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const detail = useAdmin<R360>(["restaurant", id], `/restaurants/${id}`);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [days, setDays] = useState("14");
  const [note, setNote] = useState("");
  const [done, setDone] = useState("");
  const act = useMutation({
    mutationFn: async (a: Exclude<Dialog, null> | "reactivate" | "note") => {
      if (a === "suspend") return post(`/restaurants/${id}/suspend`);
      if (a === "reactivate") return post(`/restaurants/${id}/reactivate`);
      if (a === "trial") return post(`/restaurants/${id}/trial`, { days: Number(days) });
      if (a === "nutrition") return post(`/restaurants/${id}/nutrition`);
      if (a === "ai") return post(`/restaurants/${id}/ai-reset`, { confirm: true });
      return post(`/restaurants/${id}/notes`, { body: note });
    },
    onSuccess: (_r, a) => {
      setDialog(null);
      if (a === "note") setNote("");
      setDone(
        {
          suspend: "Restaurantul a fost suspendat: meniul public nu mai apare.",
          reactivate: "Restaurantul a fost reactivat.",
          trial: `Proba a fost prelungită cu ${days} zile.`,
          nutrition: "Valorile nutriționale au fost activate pentru un an, fără plată.",
          ai: "Contorul AI a pornit de la zero.",
          note: "Nota a fost salvată.",
        }[a],
      );
      return qc.invalidateQueries({ queryKey: ["admin"] });
    },
  });
  const close = () => {
    setDialog(null);
    act.reset();
  };
  // Allergen estimates from recipes the restaurant already has: no AI call, nothing to confirm.
  const estimates = useMutation({
    mutationFn: () => post<{ products: number }>(`/restaurants/${id}/allergen-estimates`),
    onSuccess: (r) => {
      setDone(
        r.products
          ? `Alergeni estimați calculați pentru ${r.products} produse, din rețetele existente (fără AI).`
          : "Nu există produse cu rețetă fără estimare.",
      );
      return qc.invalidateQueries({ queryKey: ["admin"] });
    },
  });
  return (
    <Loadable q={detail}>
      {(d) => {
        const r = d.restaurant;
        const s = d.subscription;
        const active = r.status === "active";
        const owner = d.members.find((m) => m.role === "owner");
        const canExtend =
          s && ["trialing", "expired"].includes(String(s.status)) && !s.provider_subscription_id;
        const nutritionOn = d.nutrition.some((n) => Number(n.ends_at) > Date.now());
        const fb = d.feedback;
        return (
          <>
            <Link to="/admin/restaurante">← Restaurante</Link>
            <PageHead
              title={text(r.name)}
              sub={
                <>
                  {text(owner?.email)} · creat {day(r.created_at)} · {text(r.country)} · {text(r.currency)}
                </>
              }
              actions={
                <>
                  <SupportLoginButton id={id} />
                  <Link className="btn btn-secondary btn-sm" to={`/admin/chat/${id}`}>
                    Chat
                  </Link>
                  <a className="btn btn-secondary btn-sm" href={d.publicUrl} target="_blank" rel="noreferrer">
                    <ExternalLink {...ICON} aria-hidden="true" />
                    Meniul public
                  </a>
                  {active ? (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => setDialog("suspend")}
                    >
                      <Ban {...ICON} aria-hidden="true" />
                      Suspendă
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={act.isPending}
                      onClick={() => act.mutate("reactivate")}
                    >
                      <RotateCcw {...ICON} aria-hidden="true" />
                      Reactivează
                    </button>
                  )}
                </>
              }
            />
            {done && (
              <Alert tone="success" live>
                {done}
              </Alert>
            )}
            {act.isError && !dialog && (
              <Alert tone="error" live>
                {(act.error as Error).message}
              </Alert>
            )}
            <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
              <RestaurantChip status={r.status} />
              <SubChip status={s?.status ?? "beta"} trialEndsAt={s?.trial_ends_at} />
              {nutritionOn && <StatusChip tone="success" icon={Salad} label="Valori nutriționale active" />}
            </div>

            <div className="kpi-grid">
              <Kpi
                label="AI consumat"
                value={`${usd(d.ai.usedMicro)} / ${usd(d.ai.capMicro, 0)}`}
                tone={d.ai.usedMicro >= d.ai.capMicro * 0.9 ? "warning" : undefined}
              />
              <Kpi
                label="Deschideri meniu (30 zile)"
                value={text(d.views30?.views)}
                hint={`${text(d.views30?.qr)} din QR`}
              />
              <Kpi
                label="Feedback"
                value={Number(fb.total ?? 0)}
                hint={
                  fb.avg
                    ? `medie ${Number(fb.avg).toFixed(1).replace(".", ",")} / 5 · ${text(fb.unread)} necitite`
                    : undefined
                }
              />
              <Kpi label="Facturi" value={d.invoices.length} />
            </div>

            <Section
              title="Abonament"
              actions={
                <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={!canExtend}
                    title={canExtend ? undefined : "Doar pentru proba proprie, fără abonament Stripe"}
                    onClick={() => setDialog("trial")}
                  >
                    <CalendarPlus {...ICON} aria-hidden="true" />
                    Prelungește proba
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={nutritionOn}
                    onClick={() => setDialog("nutrition")}
                  >
                    <Salad {...ICON} aria-hidden="true" />
                    Activează valori nutriționale
                  </button>
                </div>
              }
            >
              {s ? (
                <dl className="admin-dl">
                  <dt>Stare</dt>
                  <dd>
                    <SubChip status={s.status} trialEndsAt={s.trial_ends_at} />
                  </dd>
                  <dt>Plan</dt>
                  <dd>{s.plan_id ? text(s.plan_id) : "neales"}</dd>
                  <dt>Proba se încheie</dt>
                  <dd>{day(s.trial_ends_at)}</dd>
                  <dt>Perioada plătită până la</dt>
                  <dd>
                    {day(s.current_period_end)}
                    {Number(s.cancel_at_period_end) ? " · se anulează la final" : ""}
                  </dd>
                  <dt>Stripe</dt>
                  <dd>
                    {s.provider_customer_id ? (
                      <a
                        href={`https://dashboard.stripe.com/customers/${String(s.provider_customer_id)}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Deschide clientul în Stripe
                      </a>
                    ) : (
                      "fără client Stripe"
                    )}
                  </dd>
                </dl>
              ) : (
                <p className="muted" style={{ margin: 0 }}>
                  Fără abonament: acces gratuit (cont creat înainte de plăți sau cont de test).
                </p>
              )}
              {d.billingProfile && (
                <p className="caption muted" style={{ margin: 0 }}>
                  Facturare: {text(d.billingProfile.company_name)} · {text(d.billingProfile.vat_code)} ·{" "}
                  {text(d.billingProfile.city)}
                </p>
              )}
              <Table
                head={["Data", "Produs", "Sumă", "Factură fiscală", ""]}
                empty="Nicio factură."
                rows={d.invoices.map((i) => {
                  const f = fiscalOf(i.fiscal_status);
                  return [
                    day(i.paid_at ?? i.issued_at),
                    i.product_kind
                      ? PRODUCT_LABEL[String(i.product_kind)]
                      : i.plan_interval === "year"
                        ? "Abonament anual"
                        : "Abonament lunar",
                    money(i.amount_minor, i.currency),
                    <span key="f" className="stack" style={{ gap: 2 }}>
                      <StatusChip
                        tone={f.tone}
                        icon={FileText}
                        label={
                          i.fiscal_number ? `${text(i.fiscal_series)} ${text(i.fiscal_number)}` : f.label
                        }
                      />
                      {i.fiscal_error ? <span className="caption">{text(i.fiscal_error)}</span> : null}
                    </span>,
                    i.fiscal_url ? (
                      <a key="u" href={String(i.fiscal_url)} target="_blank" rel="noreferrer">
                        PDF
                      </a>
                    ) : i.hosted_url ? (
                      <a key="u" href={String(i.hosted_url)} target="_blank" rel="noreferrer">
                        Stripe
                      </a>
                    ) : (
                      "—"
                    ),
                  ];
                })}
              />
            </Section>

            <Section
              title="Note interne"
              actions={<span className="caption muted">Doar echipa Meniova le vede.</span>}
            >
              <form
                className="row"
                style={{ gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}
                onSubmit={(e: FormEvent) => {
                  e.preventDefault();
                  if (note.trim()) act.mutate("note");
                }}
              >
                <label className="stack" style={{ gap: 4, flex: "1 1 320px" }}>
                  <span className="field-label">Notă nouă</span>
                  <textarea
                    className="field-input"
                    rows={2}
                    maxLength={2000}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Ex.: a sunat pentru factură, i-am prelungit proba cu 7 zile."
                  />
                </label>
                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={!note.trim() || act.isPending}
                >
                  <StickyNote {...ICON} aria-hidden="true" />
                  Salvează nota
                </button>
              </form>
              {d.notes.length > 0 && (
                <ul className="admin-notes">
                  {d.notes.map((n) => (
                    <li key={String(n.id)}>
                      <span className="caption muted">
                        {when(n.created_at)} · {text(n.author)}
                      </span>
                      <span style={{ whiteSpace: "pre-wrap" }}>{text(n.body)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section title="Meniuri">
              <Table
                head={["Meniu", "Stare", "Produse", "De verificat"]}
                rows={d.menus.map((m) => [
                  text(m.name),
                  m.archived_at
                    ? "arhivat"
                    : Number(m.published)
                      ? `publicat ${day(m.published_at)}`
                      : "ciornă",
                  text(m.products),
                  text(m.flagged),
                ])}
              />
            </Section>

            <Section
              title="AI"
              actions={
                <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={estimates.isPending}
                    title="Din rețetele pe care restaurantul le are deja; nu face apeluri AI"
                    onClick={() => estimates.mutate()}
                  >
                    <Salad {...ICON} aria-hidden="true" />
                    Alergeni estimați din rețete (fără AI)
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDialog("ai")}>
                    <Bot {...ICON} aria-hidden="true" />
                    Repornește contorul AI
                  </button>
                </div>
              }
            >
              <Table
                head={["Când", "Scop", "Model", "Stare", "Cost"]}
                empty="Niciun apel AI."
                rows={d.ai.recent.map((a) => [
                  when(a.created_at),
                  text(a.purpose),
                  text(
                    String(a.model ?? "")
                      .split("/")
                      .pop(),
                  ),
                  text(a.status),
                  usd(a.micro, 4),
                ])}
              />
            </Section>

            <Section title="Importuri">
              <Table
                head={["Creat", "Stare", "Pagini", "Produse", "De verificat", "Erori"]}
                rows={d.imports.map((j) => [
                  when(j.created_at),
                  IMPORT_LABEL[String(j.status)] ?? text(j.status),
                  text(j.files),
                  text(j.merged_products),
                  text(j.merged_flagged),
                  text(j.file_errors ?? j.error_code),
                ])}
              />
            </Section>

            <Section title="Echipă">
              <Table
                head={["E-mail", "Nume", "Rol", "Ultima intrare", "Stare"]}
                rows={d.members.map((m) => [
                  text(m.email),
                  text(m.name),
                  ROLE_LABEL[String(m.role)] ?? text(m.role),
                  when(m.last_session),
                  m.disabled_at ? "dezactivat" : m.email_verified_at ? "activ" : "e-mail neconfirmat",
                ])}
              />
            </Section>

            {fb.recent.length > 0 && (
              <Section title="Feedback recent">
                <Table
                  head={["Când", "Notă", "Mesaj"]}
                  rows={fb.recent.map((f) => [when(f.created_at), `${text(f.rating)} / 5`, text(f.message)])}
                />
              </Section>
            )}

            <Section title="Jurnal">
              <Table
                head={["Când", "Acțiune", "Cine"]}
                rows={d.audit.map((e) => [
                  when(e.created_at),
                  text(e.action),
                  e.actor_type === "support"
                    ? `echipa (${text(e.actor_email)})`
                    : text(e.actor_email ?? e.actor_type),
                ])}
              />
            </Section>

            {dialog && (
              <Modal
                title={
                  {
                    suspend: "Suspenzi restaurantul?",
                    trial: "Prelungești proba gratuită",
                    nutrition: "Activezi valorile nutriționale fără plată?",
                    ai: "Repornești contorul AI?",
                  }[dialog]
                }
                body={
                  {
                    suspend:
                      "Meniul public și codul QR arată „Meniul nu este disponibil”. Datele rămân; îl poți reactiva oricând.",
                    trial:
                      "Zilele se adaugă la data de final a probei (sau de azi, dacă proba s-a încheiat). Restaurantul primește din nou mementourile.",
                    nutrition:
                      "Restaurantul primește add-on-ul pentru un an, fără factură. Analizele AI ale valorilor nutriționale consumă din plafonul AI al restaurantului.",
                    ai: `Restaurantul a consumat ${usd(d.ai.usedMicro)} din ${usd(d.ai.capMicro, 0)}. După repornire poate cheltui încă până la ${usd(d.ai.capMicro, 0)} în importuri și analize AI — cost real plătit de Meniova.`,
                  }[dialog]
                }
                alert
                onClose={close}
                actions={
                  <>
                    <button type="button" className="btn btn-secondary" onClick={close}>
                      Renunță
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={act.isPending}
                      onClick={() => act.mutate(dialog)}
                    >
                      {act.isPending
                        ? "Se aplică…"
                        : {
                            suspend: "Suspendă",
                            trial: "Prelungește",
                            nutrition: "Activează",
                            ai: "Repornește contorul",
                          }[dialog]}
                    </button>
                  </>
                }
              >
                {dialog === "trial" && (
                  <label className="stack" style={{ gap: 4 }}>
                    <span className="field-label">Zile în plus (1–90)</span>
                    <input
                      className="field-input"
                      type="number"
                      min={1}
                      max={90}
                      value={days}
                      onChange={(e) => setDays(e.target.value)}
                    />
                  </label>
                )}
                {act.isError && (
                  <Alert tone="error" live>
                    {(act.error as Error).message}
                  </Alert>
                )}
              </Modal>
            )}
          </>
        );
      }}
    </Loadable>
  );
}

/**
 * „Intră în cont” (owner 2026-11-03): opens the restaurant's account in a new tab, as the Meniova team, for one hour.
 * Direct access, no notice to the restaurant; the start and every change go into the audit under the admin's name.
 * The tab is opened before the request so the browser does not block it as a pop-up.
 */
function SupportLoginButton({ id }: { id: string }) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function open() {
    setError("");
    setBusy(true);
    const tab = window.open("", "_blank");
    try {
      const { url } = await post<{ url: string }>(`/restaurants/${id}/support-login`);
      if (tab) tab.location.href = url;
      else window.location.href = url;
    } catch (e) {
      tab?.close();
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={open}>
        <LogIn {...ICON} aria-hidden="true" />
        Intră în cont
      </button>
      {error && (
        <Alert tone="error" live>
          {error}
        </Alert>
      )}
    </>
  );
}

const STAGE: Record<string, { label: string; tone: "info" | "warning" | "success"; icon: typeof Link2 }> = {
  link_sent: { label: "Link trimis", tone: "info", icon: Link2 },
  link_expired: { label: "Link expirat", tone: "warning", icon: Clock },
  claimed: { label: "Cont creat · așteaptă cardul", tone: "warning", icon: CreditCard },
  activated: { label: "Activat", tone: "success", icon: CircleCheck },
};

/**
 * Restaurants prepared by Meniova for a client (owner 2026-11-04): create one with its plan and free days, prepare
 * its menu with „Intră în cont”, send the one-time link; the list shows where each client is.
 */
function PreparedClients() {
  const qc = useQueryClient();
  const list = useAdmin<{ prepared: Row[] }>(["prepared"], "/prepared");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", city: "", planId: "standard_year", trialDays: "14" });
  const [link, setLink] = useState<{ name: string; url: string } | null>(null);
  const [error, setError] = useState("");
  const create = useMutation({
    mutationFn: () => post<{ url: string }>("/prepared", { ...form, trialDays: Number(form.trialDays) }),
    onSuccess: ({ url }) => {
      setLink({ name: form.name, url });
      setOpen(false);
      setForm({ name: "", city: "", planId: "standard_year", trialDays: "14" });
      void qc.invalidateQueries({ queryKey: ["prepared"] });
      void qc.invalidateQueries({ queryKey: ["restaurants"] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });
  const renew = useMutation({
    mutationFn: (r: Row) =>
      post<{ url: string }>(`/restaurants/${String(r.id)}/claim-link`).then((x) => ({
        ...x,
        name: text(r.name),
      })),
    onSuccess: ({ url, name }) => {
      setLink({ name, url });
      void qc.invalidateQueries({ queryKey: ["prepared"] });
    },
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    setError("");
    create.mutate();
  }
  const waText = (url: string) => `Meniul tău Meniova e pregătit. Activează-l aici: ${url}`;
  return (
    <Section
      title="Restaurante pregătite pentru clienți"
      actions={
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setOpen(true)}>
          Restaurant nou pentru un client
        </button>
      }
    >
      <p className="caption muted" style={{ margin: 0 }}>
        Creezi restaurantul cu planul și zilele gratuite, îi pregătești meniul cu „Intră în cont”, apoi îi
        trimiți clientului linkul. El își face contul, completează firma și adaugă cardul (0 € azi); meniul
        devine public după card.
      </p>
      {link && (
        <Alert tone="success" live>
          <strong>Link pentru {link.name}</strong> (valabil 7 zile, o singură folosire):{" "}
          <code style={{ overflowWrap: "anywhere" }}>{link.url}</code>{" "}
          <button
            type="button"
            className="link-btn"
            onClick={() => void navigator.clipboard.writeText(link.url)}
          >
            Copiază
          </button>{" "}
          <a
            href={`https://wa.me/?text=${encodeURIComponent(waText(link.url))}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Trimite pe WhatsApp
          </a>
        </Alert>
      )}
      {renew.isError && (
        <Alert tone="error" live>
          {renew.error.message}
        </Alert>
      )}
      <Loadable q={list}>
        {(d) => (
          <Table
            empty="Niciun restaurant pregătit încă."
            head={["Restaurant", "Stadiu", "Plan", "Produse", "Proprietar", "Pregătit", "Acțiuni"]}
            rows={d.prepared.map((r) => {
              const st = STAGE[String(r.stage)] ?? STAGE.link_sent;
              return [
                <Link key="n" to={`/admin/restaurante/${String(r.id)}`} style={{ fontWeight: 600 }}>
                  {text(r.name)}
                </Link>,
                <StatusChip
                  key="s"
                  tone={st?.tone ?? "info"}
                  icon={st?.icon ?? Link2}
                  label={st?.label ?? ""}
                />,
                r.plan_choice === "standard_month" ? "Lunar" : "Anual",
                text(r.products),
                text(r.owner_email),
                day(r.prepared_at),
                <div key="a" className="row" style={{ gap: 6 }}>
                  {r.stage !== "activated" && <SupportLoginButton id={String(r.id)} />}
                  {(r.stage === "link_sent" || r.stage === "link_expired") && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      disabled={renew.isPending}
                      onClick={() => renew.mutate(r)}
                    >
                      Link nou
                    </button>
                  )}
                </div>,
              ];
            })}
          />
        )}
      </Loadable>
      {open && (
        <Modal
          title="Restaurant nou pentru un client"
          body="Restaurantul nu are încă proprietar. Meniul îl pregătești tu; clientul îl preia cu linkul."
          onClose={() => setOpen(false)}
          actions={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setOpen(false)}>
                Renunță
              </button>
              <button type="submit" form="prep-form" className="btn btn-primary" disabled={create.isPending}>
                {create.isPending ? "Se creează…" : "Creează și dă-mi linkul"}
              </button>
            </>
          }
        >
          <form id="prep-form" className="stack" style={{ gap: 12 }} onSubmit={submit}>
            {error && (
              <Alert tone="error" live>
                {error}
              </Alert>
            )}
            <label className="field">
              <span className="field-label">Numele restaurantului</span>
              <input
                className="field-input"
                required
                maxLength={80}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </label>
            <label className="field">
              <span className="field-label">Orașul (opțional)</span>
              <input
                className="field-input"
                maxLength={80}
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
              />
            </label>
            <label className="field">
              <span className="field-label">Planul presetat</span>
              <select
                className="field-input"
                value={form.planId}
                onChange={(e) => setForm({ ...form, planId: e.target.value })}
              >
                <option value="standard_year">Anual · 99 € primul an, apoi 149 € + TVA</option>
                <option value="standard_month">Lunar · 19,90 € + TVA</option>
              </select>
            </label>
            <label className="field">
              <span className="field-label">Zile gratuite (de la preluare; plata de azi e 0 €)</span>
              <input
                className="field-input"
                type="number"
                min={1}
                max={90}
                inputMode="numeric"
                value={form.trialDays}
                onChange={(e) => setForm({ ...form, trialDays: e.target.value })}
              />
            </label>
          </form>
        </Modal>
      )}
    </Section>
  );
}
